import {
  changeOrganizationUserRoleCloudFunction,
  createInvitationCloudFunction,
  createInvitationsCloudFunction,
  createOrganizationCloudFunction,
  db,
  deleteOrganizationCloudFunction,
  deleteOrganizationPermissionsCloudFunction,
} from '@/firebase/init';
import {
  collection,
  getDocs,
  query,
  where,
  documentId,
  doc,
  getDoc,
  orderBy,
  deleteDoc,
  runTransaction,
  updateDoc,
} from 'firebase/firestore';
import { OrganizationFromStore } from '@/types/Organization';
import { isUserRole } from '@/utils/typeGuards';
import { differenceBy } from 'lodash';
import { InvitationFromStore, PermissionFromStore, UserRole } from '@/types/types';
import { WorkspaceFromStore } from '@/types/workspaces';
import { getOrganizationPermissionsPath } from '@/utils/queryPaths';
import { UserService } from './userService';

export class OrganizationsService {
  static async createOrganization(
    organizationName: string,
    region: string,
    priceId: string,
  ) {
    return await createOrganizationCloudFunction({ organizationName, region, priceId });
  }

  static async getUserPermission(organizationId: string, userId: string) {
    const snapshot = await getDoc(
      doc(db, `organizations/${organizationId}/organization_permissions/${userId}`),
    );
    if (!snapshot.exists()) return null;
    return { id: snapshot.id, ...snapshot.data() } as PermissionFromStore;
  }

  static async getOrganizationsByIds(organizationIds: string[]) {
    if (organizationIds.length === 0) return [] as OrganizationFromStore[];
    const qry = query(
      collection(db, 'organizations'),
      where(documentId(), 'in', organizationIds),
    );
    const snapshots = await getDocs(qry);
    return snapshots.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as OrganizationFromStore[];
  }

  static async getOrganizations(userId: string) {
    const [organizationIds] = await UserService.getEntitiesIds(userId);
    if (organizationIds.length === 0) return [] as OrganizationFromStore[];
    const qry = query(
      collection(db, 'organizations'),
      where(documentId(), 'in', organizationIds),
    );
    const snapshots = await getDocs(qry);
    return snapshots.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as OrganizationFromStore[];
  }

  static async deleteOrganization(organizationId: string, region: string) {
    await deleteOrganizationCloudFunction({ organizationId, region });
  }

  static async getOrganizationWorkspaces(organizationId: string) {
    const qry = query(
      collection(db, 'workspaces'),
      where('organizationId', '==', organizationId),
    );
    const snapshots = await getDocs(qry);
    return snapshots.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as WorkspaceFromStore[];
  }

  static async createInvitation(organizationId: string, email: string, role: UserRole) {
    await createInvitationCloudFunction({
      email,
      entityId: organizationId,
      entityType: 'organizations',
      role,
    });
    return null;
  }

  static async createInvitations(
    organizationId: string,
    emails: string[],
    role: UserRole,
  ) {
    await createInvitationsCloudFunction({
      emails,
      entityId: organizationId,
      entityType: 'organizations',
      role,
    });
    return null;
  }

  static async getInvitations(organizationId: string) {
    const qry = query(
      collection(db, 'organizations', organizationId, 'organization_invitations'),
    );
    const snapshots = await getDocs(qry);
    return snapshots.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as InvitationFromStore<'organization'>[];
  }

  static async getPermissions(organizationId: string) {
    const permissions: PermissionFromStore[] = [];
    const snapshot = await getDocs(
      query(
        collection(db, getOrganizationPermissionsPath(organizationId)),
        orderBy('createdAt', 'asc'),
      ),
    );
    snapshot.forEach((permission) => {
      permissions.push({
        ...permission.data(),
        id: permission.id,
      } as PermissionFromStore);
    });
    return permissions;
  }

  static async deletePermission(permissionId: string, organizationId: string) {
    await deleteOrganizationPermissionsCloudFunction({
      organizationId,
      usersIds: [permissionId],
    });
    return null;
  }

  static async deleteInvitation(hash: string, organizationId: string) {
    const invitationsSnap = await getDocs(
      query(
        collection(db, `organizations/${organizationId}/organization_invitations`),
        where('hash', '==', hash),
      ),
    );
    const invitation = invitationsSnap.docs[0];
    if (invitation) {
      await deleteDoc(invitation.ref);
    }

    return null;
  }

  static async deleteSelectedUsers(
    organizationId: string,
    selected: {
      id: string;
      role: UserRole | string;
      hash?: string;
    }[],
  ) {
    const selectedPermissions = selected.filter((item) => isUserRole(item.role));
    const selectedInvitations = differenceBy(selected, selectedPermissions, 'id');

    const fetchedInvitations = await Promise.all(
      selectedInvitations.map(async (inv) => {
        const snap = await getDocs(
          query(
            collection(db, `organizations/${organizationId}/organization_invitations`),
            where('hash', '==', inv.hash),
          ),
        );
        return snap.docs[0];
      }),
    );
    await deleteOrganizationPermissionsCloudFunction({
      organizationId,
      usersIds: selectedPermissions.map(({ id }) => id),
    });
    await runTransaction(db, async (transaction) => {
      fetchedInvitations.forEach((invitation) => {
        transaction.delete(invitation.ref);
      });
    });

    return null;
  }

  static async updateName(organizationId: string, organizationName: string) {
    await updateDoc(doc(db, `organizations/${organizationId}`), {
      name: organizationName,
    });
    return null;
  }

  static async changeRole(organizationId: string, permissionId: string, role: UserRole) {
    await changeOrganizationUserRoleCloudFunction({
      organizationId,
      role,
      userId: permissionId,
    });
    return null;
  }

  static async getSubscriptionDetails(organizationId: string) {
    const subscriptionRef = doc(
      db,
      `organizations/${organizationId}/subscription/subscription`,
    );
    const subscriptionSnap = await getDoc(subscriptionRef);
    if (!subscriptionSnap.exists()) return null;
    return { ...subscriptionSnap.data(), id: subscriptionSnap.id };
  }

  static async changeInvitationRole(
    organizationId: string,
    invitationId: string,
    role: string,
  ) {
    await updateDoc(
      doc(db, `organizations/${organizationId}/organization_invitations/${invitationId}`),
      {
        role,
      },
    );
    return null;
  }
}
